import React, { useEffect, useState } from 'react';
import { Button } from '../inputs/button';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete.svg';
import { ReactComponent as AddIcon } from '../../../assets/icons/add.svg';
import { ReactComponent as AddNoteIcon } from '../../../assets/icons/add-note.svg';
import { ReactComponent as AddScheduleIcon } from '../../../assets/icons/add-schedule.svg';
import { ReactComponent as EditScheduleIcon } from '../../../assets/icons/edit-schedule.svg';
import { ReactComponent as ImportIcon } from '../../../assets/icons/import.svg';
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { DeleteResource } from '../actions/deleteResource';
import { Api, pathJoin } from '../../../services/api';
import { Modal } from '../modal/modal';
import { Notify } from '../../../services/notification/notify';
import { ScheduleCard } from '../cards/scheduleCard';
import { UserList } from '../../feature/users/userList';
import { Load } from '../load/load';
import { InfoCard } from '../cards/infoCard';
import { SelectQuery } from '../inputs/selectQuery';
import { Switch } from '../inputs/switch';
import { useHasPermission } from '../../../services/auth/auth';
import { SystemList } from '../../feature/systemAccess/systemList';
import { QueryGridSelectMode } from './../grid/queryGrid/queryGrid';
import { BRANCH, ZIPCODE } from '../../../permissions';

export const Import = ({canWrite}) => {
    const location = useLocation();
    
    return canWrite ? <Link to={pathJoin(location.pathname, 'import')}>
        <Button type='button' title='Import' className='icon'><ImportIcon /></Button>
    </Link>
    : <></>
}


export const AddSchedule = ({ids, name, refreshData, resourceName}) => {

    const canWrite = useHasPermission([ZIPCODE.WRITE, BRANCH.READ, BRANCH.WRITE]);

    const [show, setShow] = useState(false);
    const [schedule, setSchedule] = useState({
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false
    });
    
    /** @type [object, function(object): void] */
    const [selectedBranch, setSelectedBranch] = useState({});


    const add = () => {
        Api.post('/locations/schedules', ids.map(id => ({
            branchId: selectedBranch.id,
            zipcodeId: id,
            ...schedule
        })))
        .then(json => {
            if (json.success) refreshData();
            else throw new Error(json.message);
        })
        .catch(err => {
            console.error('Error adding the schedule', err);
            Notify.error('Error Adding Schedule', 'There was an issue while adding the schedule and the action couldn\'t be completed.')
        })
    }

    const close = () => {
        setShow(false);

        window.setTimeout(() => {
            setSchedule({
                monday: false,
                tuesday: false,
                wednesday: false,
                thursday: false,
                friday: false
            });
            setSelectedBranch({});
        }, 250);
    }

    return canWrite ? <>
        <Modal
            active={show}
            onClose={close}
            header='Add Schedule'
            width='650px'
            buttons={[{label: 'Cancel', primary: false, closeOnClick: true}, {label: 'Add', primary: true, closeOnClick: true, onClick: () => { add(); }}]}
        >
            <label>
                Branch<br /><br />
                <SelectQuery
                    value={selectedBranch}
                    onChange={setSelectedBranch}
                    pluralResourceName='Branches'
                    restEndpoint='/locations/branches'
                />
            </label>

            <br /><br />

            {

                selectedBranch && selectedBranch.name &&
                <ScheduleCard 
                    title={<>{selectedBranch.name}'s Schedule</>}
                    editMode={true}
                    // @ts-ignore
                    schedule={schedule}
                    onChange={v => setSchedule(v)}
                />
            }


            <br />
            <br />
            {resourceName} to affect: {name}
        </Modal>

        <Button
            type='button'
            title='Add Schedule'
            className='icon'
            onClick={() => setShow(true)}
            disabled={ids?.length === 0}
        ><AddScheduleIcon /></Button>
    </> : <></>
}



export const EditSchedule = ({ids, name, canWrite, refreshData, data, resourceName, nameFunction}) => {

    const [show, setShow] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [action, setAction] = useState('update');
    const [affectedZipcodes, setAffectedZipcodes] = useState([]);

    const [schedule, setSchedule] = useState({
        id: null,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false
    });

    useEffect(() => {
        setAffectedZipcodes(getAffectedZipcodes())
    }, [data, ids, action, selectedBranch]);

    const getAffectedZipcodes = () => {
        return data.filter(z => (
            ids.indexOf(z?.id) !== -1 && (
                action === 'updateAndAdd' || (
                    (action === 'update' || action === 'delete') && 
                    (z?.schedules ?? []).some(s => s.branch?.id === selectedBranch?.id)
                )
            )
        ));
    }

    const save = () => {

        let scheduleIdsToPatch = [];
        let zipcodeIdsToPost = [];

        for (let i = 0; i < data.length; i++) {
            
            if (ids.indexOf(data[i]?.id) === -1) continue;

            let hasSchedule = false;
            
            (data[i]?.schedules ?? [])?.forEach(s => {
                if (s.branch?.id === selectedBranch?.id && s?.id) {
                    hasSchedule = true;
                    scheduleIdsToPatch.push(s?.id)
                }
            });

            if (!hasSchedule)
                zipcodeIdsToPost.push(data[i]?.id);
        }

        if (scheduleIdsToPatch.length > 0 && (action === 'update' || action === 'updateAndAdd')) {
            Api.patch(
                pathJoin('/locations/schedules', scheduleIdsToPatch.join(',')),
                schedule
            )
            .then(json => {
                if (!json.success) throw new Error(json.message);
            })
            .catch(err => {
                console.error('Error updating schedule', err);
                Notify.error('Error Updating Schedule', 'There was an issue while updating the schedule and the action couldn\'t be completed.')
            });
        }

        if (scheduleIdsToPatch.length > 0 && action === 'delete') {
            Api.delete(pathJoin('/locations/schedules', scheduleIdsToPatch.join(',')))
            .then(json => {
                if (json.success) refreshData();
                else throw new Error(json.message);
            })
            .catch(err => {
                console.error('Error deleting schedules', err);
                Notify.error('Error Deleting Schedule', 'There was an issue while deleting the schedule and the action couldn\'t be completed.')
            });
        }

        if (action === 'updateAndAdd') {
            Api.post(
                '/locations/schedules',
                zipcodeIdsToPost.map(id => ({
                    branchId: selectedBranch.id,
                    zipcodeId: id,
                    ...schedule
                }))
            )
            .then(json => {
                if (json.success) refreshData();
                else throw new Error(json.message);
            })
            .catch(err => {
                console.error('Error updating the schedule', err);
                Notify.error('Error Updating Schedule', 'There was an issue while updating the schedule and the action couldn\'t be completed.')
            })
        }
    }

    const resetSchedule = () => {
        setSchedule({
            id: null,
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false
        });
    }

    const open = () => {
        // setSchedules()
        setShow(true);
    }

    const close = () => {
        resetSchedule();
        setShow(false);
    }

    const handleChange = (v) => {
        if (v?.id != selectedBranch?.id) {
            setSelectedBranch(v);
            resetSchedule();
        }
    }


    return canWrite ? <>
        <Modal
            width='650px'
            active={show}
            onClose={() => { close(); }}
            header={'Edit Schedules'}
            buttons={[
                {
                    label: 'Cancel',
                    primary: false,
                    closeOnClick: true,
                },
                {
                    label: 'Save',
                    primary: true,
                    closeOnClick: true,
                    disabled: !selectedBranch || getAffectedZipcodes().length === 0,
                    onClick: () => { save(); },
                }
            ]}
        >
            <label>
                Branch<br /><br />
                <SelectQuery
                    value={selectedBranch}
                    onChange={handleChange}
                    pluralResourceName='Branches'
                    restEndpoint='/locations/branches'
                />
            </label>

            <br /><br />

            <InfoCard title='Action Options'>
                <>
                    <ul>
                        <li><strong>Update:</strong> Updates the schedules for zip codes that have that branch.</li>
                        <li><strong>Add or Update:</strong> Updates the schedules for zip codes that have that branch and adds the schedule for zip codes that do not.</li>
                        <li><strong>Delete:</strong> Deletes schedules for zip codes that have that branch.</li>
                    </ul>
                </>
            </InfoCard>

            <br /><br />

            <label>
                Action<br /><br />
                <Switch
                    data={[
                        {
                            label: 'Update',
                            value: 'update'
                        },
                        {
                            label: 'Add or Update',
                            value: 'updateAndAdd',
                        },
                        {
                            label: 'Delete',
                            value: 'delete',
                        },
                    ]}
                    initialValue={action}
                    onChange={setAction}
                
                />
            </label>

            <br /><br />

            {selectedBranch ? <>
                {action !== 'delete' && <>
                    <ScheduleCard
                        title={<>{selectedBranch?.name}'s Schedule</>}
                        editMode={true}
                        schedule={schedule}
                        onChange={setSchedule}
                    />
                    
                    <br /><br />
                </>}

                {resourceName} to affect: {affectedZipcodes.map(z => nameFunction(z)).join(', ')} {affectedZipcodes.length === 0 && 'None'}
                    
            </> : <div>Select a branch</div>}
        </Modal>

        <Button
            type='button'
            title='Edit Schedules'
            className='icon'
            onClick={() => { open(); }}
            disabled={ids?.length === 0}
        ><EditScheduleIcon /></Button>
    </> : <></>
}

export const Add = ({canWrite, listUrl}) => {
    return canWrite ?
    <Link to={pathJoin(listUrl, 'add')}>
        <Button className='icon primary' type='button' title='Add new'><AddIcon /></Button>
    </Link>
    : <></>
}


export const AddNote = ({canWrite, refreshData, restEndpoint, data, ids}) => {
    const addNote = () => {
        Api.patch(pathJoin(restEndpoint, ids.join(',')), {note: ' '})
            .then(json => {
                if (!json.success)
                    throw new Error('Response was not a success');

                refreshData();
            })
            .catch(e => {
                console.error('Error adding note', e);
                Notify.error('Error Adding Note', 'There was an issue adding the note.');
            });
    }


    return canWrite &&
        <Button
            className='icon'
            type='button'
            title='Add Note'
            onClick={() => addNote()}
            disabled={data.every(d => !!d.note)}
        >
            <AddNoteIcon />
        </Button>
}


export const AddAuthorizedUser = ({canWrite, refreshData, scope, data}) => {
    const [showUsers, setShowUsers] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const addUser = (u) => {
        setLoading(true);
        Api.post(pathJoin('/systemAccess', scope, 'authorizedUsers', u.id))
        .then(json => {
            if (!json.success)
                throw new Error('Response was not a success');

            refreshData();
            setLoading(false);
            setSelectedUser(null);
            setShowUsers(false);
        })
        .catch(e => {
            setLoading(false);
            setShowUsers(false);
            console.error('Error adding authorized user', e);
            Notify.error('Error Adding Authorized User', 'There was an issue adding an authorized user.');
        })
    }


    return canWrite ? <>
        <Modal
            active={showUsers}
            header='Add Authorized User'
            width='800px'
            buttons={[
                {label: 'Close', primary: false, closeOnClick: true},
                {label: 'Add', primary: true, closeOnClick: true, onClick: () => addUser(selectedUser), disabled: !selectedUser},
            ]}
            onClose={() => { if (!loading) setShowUsers(false); }}
        >
            {
                loading
                ? <Load size={65} />
                : <>
                    <SelectQuery
                        placeholder='Select User...'
                        value={selectedUser}
                        onChange={setSelectedUser}
                        pluralResourceName='Users'
                        restEndpoint='/users'
                        nameFunction={u => u.displayName}
                    />
                    <br /><br />

                    <InfoCard title='Details'>
                        <>
                            You are about to add the user {selectedUser?.displayName ?? 'None'} as accessible to the system. This change will be audited.
                        </>
                    </InfoCard>
                </>
            }



        </Modal>

        <Button className='icon primary' type='button' title='Add new' onClick={() => setShowUsers(true)}><AddIcon /></Button>
    </>
    : <></>
}


export const AddSystemAuthorization = ({canWrite, refreshData, scope}) => {
    const [showSystems, setShowSystems] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedSystem, setSelectedSystem] = useState(null);

    const addSystem = (h) => {
        setLoading(true);
        Api.post(pathJoin('systemAccess', h.id, 'authorizedUsers', scope))
        .then(json => {
            if (!json.success)
                throw new Error('Response was not a success');

            refreshData();
            setLoading(false);
            setShowSystems(false);
        })
        .catch(e => {
            setLoading(false);
            setShowSystems(false);
            console.error('Error adding system accessibility', e);
            Notify.error('Error Adding System Accessibility', 'There was an issue adding a system as accessible for user.');
        })
    }


    return canWrite ? <>
        <Modal
            active={showSystems}
            header='Add System Accessibility'
            width='800px'
            buttons={[
                {label: 'Close', primary: false, closeOnClick: true},
                {label: 'Add', primary: true, closeOnClick: true, onClick: () => addSystem(selectedSystem), disabled: !selectedSystem},
            ]}
            onClose={() => { if (!loading) setShowSystems(false); }}
        >
            {
                loading
                ? <Load size={65} />
                : <>
                    <SelectQuery
                        placeholder='Select System...'
                        value={selectedSystem}
                        onChange={setSelectedSystem}
                        pluralResourceName='Systems'
                        restEndpoint='/systemAccess'
                    />

                    <br /><br />

                    <InfoCard title='Details'>
                        <>
                            You are about to add the system {selectedSystem?.name ?? 'None'} as accessible for the current user. This change will be audited.
                        </>
                    </InfoCard>
                </>
            }



        </Modal>

        <Button className='icon primary' type='button' title='Add new' onClick={() => setShowSystems(true)}><AddIcon /></Button>
    </>
    : <></>
}

export const Edit = ({ids, canWrite, resourceName, listUrl}) => canWrite ?
<Link to={(ids.length === 0) ? '#' : pathJoin(listUrl, ids.join(','), 'edit')}>
    <Button
        disabled={ids.length === 0}
        type='button'
        title={ids.length === 0 ? `Select ${resourceName} First` : 'Edit'}
        className='icon'
    ><EditIcon /></Button>
</Link>
: <></>

export const EditInline = ({
    canWrite,
    isEditing,
    setIsEditing,
    setLoading,
    setDisablePaging
}) => canWrite ?
    <Button
        disabled={isEditing}
        type='button'
        title='Edit'
        onClick={() => {
            setIsEditing(!isEditing);
            setDisablePaging(true);
        }}
        className='icon'
    ><EditIcon /></Button>
: <></>

export const Delete = ({
    name,
    ids,
    resourceName,
    restEndpoint,
    setSelected,
    mode,
    refreshData,
    canWrite,
    listUrl
}) => {

    const [promptDelete, setPromptDelete] = useState(false);
    const navigate = useNavigate();

    return canWrite ? <>
        <DeleteResource
            resourceName={resourceName}
            name={name}
            restEndpoint={restEndpoint}
            ids={ids}
            successCallback={() => {
                setPromptDelete(false);
                if (mode === ActionButtonModes.Query) {
                    setSelected([]);
                    refreshData();
                } else if (mode === ActionButtonModes.View) {
                    navigate(listUrl)
                }
            }}
            errorCallback={() => setPromptDelete(false)}
            cancelCallback={() => setPromptDelete(false)}
            prompt={promptDelete}
        />
    
        <Button
            disabled={ids?.length === 0}
            type='button'
            title={ids.length === 0 ? `Select ${resourceName} First` : 'Delete'}
            className='icon'
            onClick={() => {setPromptDelete(true)}}
        ><DeleteIcon /></Button>
    </> : <></>
}


/**
 * @typedef {number} ActionButtonModesEnum
 * @enum {ActionButtonModesEnum}
 */
 export const ActionButtonModes = {
    Query: 1,
    View: 2
}